import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import { useTheme } from '@mui/material/styles';
import { NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import * as NextRouter from 'next/router';
import { KeyboardEvent } from 'react';
import { StCardDesc, StCardSubTitle, StCardTitle } from '../shared/Carousel/Carousel.styles';
import { ItemListingCardProps } from './ItemListing.type';
import ButtonSecondary from '../shared/Button/SecondaryButton';
import { UIButton } from '../shared/UIButton.styles';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';

function ItemListingCard(props: ItemListingCardProps): JSX.Element {
  const theme = useTheme();
  const router = NextRouter.useRouter();
  const { isToapplyV2Theme } = useApplyV2Theme();
  const card = props.card;

  if (!card?.title) return <></>;

  const showCta = Boolean(card.linkHref && card.linkText);
  const isCardClickable = Boolean(card.linkHref && !card.linkText);

  const imageField = card.imageSrc
    ? { value: { src: card.imageSrc, alt: card.imageAlt || 'image' } }
    : undefined;

  function navigateToCard(): void {
    if (card.linkHref) router.push(card.linkHref);
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') navigateToCard();
  }

  return (
    <Card
      role={isCardClickable ? 'button' : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      aria-label={isCardClickable ? card.title : undefined}
      onClick={isCardClickable ? navigateToCard : undefined}
      onKeyDown={isCardClickable ? handleKeyDown : undefined}
      sx={{
        width: '100%',
        borderRadius: '0.6rem',
        boxShadow: `${theme.palette.neutral4.main} 0px 4px 12px`,
        position: 'relative',
        cursor: isCardClickable ? 'pointer' : undefined,
      }}
    >
      {imageField && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', height: 'auto' }}>
            <NextImage
              field={imageField}
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
        </Box>
      )}

      <CardContent sx={{ display: 'flex', flexDirection: 'column', rowGap: '1rem' }}>
        {card.condition && <StCardTitle>{card.condition}</StCardTitle>}
        <StCardSubTitle limit={props?.titleLineLimit || 2}>{card.title}</StCardSubTitle>
        {card.description && (
          <StCardDesc limit={props?.descriptionLineLimit || 3}>{card.description}</StCardDesc>
        )}
      </CardContent>
      {showCta && (
        <CardActions sx={{ m: '1rem', p: 0 }}>
          {isToapplyV2Theme ? (
            <ButtonSecondary
              fullWidth
              onClick={() => {
                window.open(card.linkHref, '_blank', 'noopener,noreferrer');
              }}
            >
              {card.linkText}
            </ButtonSecondary>
          ) : (
            <UIButton
              variant={'outlined'}
              sx={{ width: '100%', padding: '5px' }}
              href={card.linkHref}
            >
              {card.linkText}
            </UIButton>
          )}
        </CardActions>
      )}
    </Card>
  );
}

export default ItemListingCard;
